import { apiClient } from "@/lib/api-client";
/**
 * Loop 验证台(MVP)API · /admin/v1/loop-lab/*
 * 跑测试集(离线确定性 / 真 LLM)、看 trace、维护三节点提示词、试一句。
 * LLM 全集跑要几分钟,这些请求单独放大 timeout(默认 client 仅 10s)。
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

const LONG = 600_000; // 10min,够跑满 18 用例 × N1/N2/N3

export type TraceStep = {
	decide: string;
	skill: string;
	observe: string;
	params?: Record<string, unknown>;
	reason?: string; // 主 Agent 本轮为何这么决策(过程还原)
};

/** 节点级执行还原:N1/N2/N3/接续(LLM)+ B2.5/G1/G6/G4(规则)+ 工具(完整 I/O)。 */
export type NodeEvent = {
	seq: number;
	round: number;
	node: string; // n1_intent / n2_decide / n3_generate / n1_resume / B2.5 / G1 / G4 / G6 / offline / skill 名
	kind: "llm" | "rule" | "tool" | "note";
	title: string;
	ms: number; // 该环节实测耗时(毫秒)
	input: Record<string, unknown>;
	output: Record<string, unknown>;
	// LLM 节点该次调用的 token 用量(输入/输出/合计);非 LLM 节点无此字段
	usage?: { prompt_tokens: number; completion_tokens: number; total_tokens: number };
};

/** 结构化输出:有序块(每块自带 kind,前端逐块渲染;卡片数据来自 Skill 真实返回)*/
export type TextBlock = { kind: "text"; text: string };
export type ProductCard = { kind: "product"; productId: string; title: string; price: number };
export type CouponCard = {
	kind: "coupon";
	code: string;
	type?: string;
	amount?: number;
	desc?: string;
};
export type ImageBlock = { kind: "image"; url: string };
export type OutputBlock = TextBlock | ProductCard | CouponCard | ImageBlock;

/** 轮级耗时分桶 + token 分桶(输入/输出/合计,按节点 usage 求和) */
export type Timing = {
	total_ms?: number;
	llm_ms?: number;
	tool_ms?: number;
	rule_ms?: number;
	llm_calls?: number;
	tokens_in?: number; // 输入 token 合计
	tokens_out?: number; // 输出 token 合计
	tokens_total?: number; // 输入+输出 合计(每次运行消耗)
};

export type StatBucket = { count: number; avg: number; p50: number; p95: number; max: number };
export type TimingStats = {
	samples: number;
	total_ms: StatBucket;
	buckets: Record<string, StatBucket>;
	by_node: Record<string, StatBucket & { kind: string }>;
};

/** 统计时间窗口(天):默认近 14 天;0=全部。所有统计子页签共用。
 * 默认 3 天时,只要两三天没跑批整页就是 `--`,被误读成「统计坏了」;跑批本身是
 * 按天成簇的,3 天窗口经常整窗为空。14 天覆盖一个迭代周期,又不至于拖慢窗口读。 */
export const DEFAULT_STATS_DAYS = 14;

export function useTiming(days = DEFAULT_STATS_DAYS) {
	return useQuery<TimingStats>({
		queryKey: ["loop-lab", "timing", days],
		queryFn: async () => {
			const { data } = await apiClient.get<TimingStats>("/admin/v1/loop-lab/timing", {
				params: { days },
			});
			return data;
		},
		refetchInterval: 30_000, // 看趋势,≤30s 自动刷新(不必手动点)
	});
}

/** Token 用量聚合 · 每次大模型节点运行(N1/N2/N3/演员/判官/归因)的 token 明细统计 */
export type TokenAgg = {
	calls: number; // 调用次数
	tokens_in: number; // 输入
	tokens_out: number; // 输出
	tokens_total: number; // 输入+输出 合计
	avg_total: number; // 每次调用均值
};
export type TokenStatsRow = TokenAgg & { key: string };
export type TokenStats = {
	samples: number;
	overall: TokenAgg;
	by_stage: TokenStatsRow[]; // 按节点:n1_intent/n2_decide/n3_generate/actor/judge/rca
	by_model: TokenStatsRow[];
	by_date: TokenStatsRow[];
};

export function useTokenStats(days = DEFAULT_STATS_DAYS) {
	return useQuery<TokenStats>({
		queryKey: ["loop-lab", "token-stats", days],
		queryFn: async () => {
			const { data } = await apiClient.get<TokenStats>("/admin/v1/loop-lab/token-stats", {
				params: { days },
			});
			return data;
		},
		refetchInterval: 30_000,
	});
}

/** Token 明细:每次大模型节点运行一条 */
export type TokenRecord = {
	ts: string;
	stage: string;
	model: string;
	prompt_tokens: number;
	completion_tokens: number;
	total_tokens: number;
	ms: number;
};
export function useTokenRecords(days = DEFAULT_STATS_DAYS, stage = "", model = "") {
	return useQuery<{ records: TokenRecord[] }>({
		queryKey: ["loop-lab", "token-records", days, stage, model],
		queryFn: async () => {
			const params: Record<string, unknown> = { days };
			if (stage) params.stage = stage;
			if (model) params.model = model;
			const { data } = await apiClient.get<{ records: TokenRecord[] }>(
				"/admin/v1/loop-lab/token-records",
				{ params },
			);
			return data;
		},
		refetchInterval: 30_000,
	});
}

/** 应答效果聚合(仅真 LLM)·按 意图/skill/客户/轮次 分组,执行/正确/错误/正确率 */
export type AnswerStatRow = {
	key: string;
	total: number;
	pass: number;
	fail: number;
	rate: number | null;
};
export type AnswerStats = {
	samples: number;
	overall: { total: number; pass: number; fail: number; rate: number | null };
	by_intent: AnswerStatRow[];
	by_skill: AnswerStatRow[];
	by_customer: AnswerStatRow[];
	by_multi: AnswerStatRow[];
};

export function useAnswerStats(days = DEFAULT_STATS_DAYS) {
	return useQuery<AnswerStats>({
		queryKey: ["loop-lab", "answer-stats", days],
		queryFn: async () => {
			const { data } = await apiClient.get<AnswerStats>("/admin/v1/loop-lab/answer-stats", {
				params: { days },
			});
			return data;
		},
		refetchInterval: 30_000,
	});
}

/** 场景评测跨批次聚合 · pass 率/一票否决三维/persona 稳健性/阶段·意图·难度分组/终止/pass^k */
export type EvalAgg = {
	n: number;
	avg_reward: number | null;
	action: number | null;
	communicate: number | null;
	nl: number | null;
	committed_rate: number | null;
	actor_violation_rate: number | null;
	avg_turns: number | null;
};
export type EvalStatRow = EvalAgg & { key: string };
export type EvalStats = {
	samples: number;
	overall: EvalAgg;
	by_stage: EvalStatRow[];
	by_intent: EvalStatRow[];
	by_difficulty: EvalStatRow[];
	by_persona: EvalStatRow[];
	by_category: EvalStatRow[];
	termination: Record<string, number>;
	failed_dims: Record<string, number>;
	pass_k_curve: Record<string, number>;
};
export function useEvalStats(days = DEFAULT_STATS_DAYS) {
	return useQuery<EvalStats>({
		queryKey: ["loop-lab", "eval-stats", days],
		queryFn: async () => {
			const { data } = await apiClient.get<EvalStats>("/admin/v1/loop-lab/eval-stats", {
				params: { days },
			});
			return data;
		},
		refetchInterval: 30_000,
	});
}

/** 归因分布 · 簇/主归因/节点位次分布 + 需复核率 + 平均置信 */
export type RcaDistRow = { key: string; count: number };
export type RcaStats = {
	samples: number;
	overall: { n: number; needs_review_rate: number | null; avg_confidence: number | null };
	by_cluster: RcaDistRow[];
	by_primary_label: RcaDistRow[];
	by_node_locus: RcaDistRow[];
};
export function useRcaStats(days = DEFAULT_STATS_DAYS) {
	return useQuery<RcaStats>({
		queryKey: ["loop-lab", "rca-stats", days],
		queryFn: async () => {
			const { data } = await apiClient.get<RcaStats>("/admin/v1/loop-lab/rca-stats", {
				params: { days },
			});
			return data;
		},
		refetchInterval: 30_000,
	});
}

export type CaseResult = {
	id: string;
	desc: string;
	pass: boolean;
	fails: string[];
	reply: string;
	intent?: string | null;
	calls: string[];
	stop?: string | null;
	clarify?: string | null;
	trace: TraceStep[];
	node_trace?: NodeEvent[];
	timing?: Timing;
	blocks?: OutputBlock[];
};

export type RunReport = {
	total: number;
	passed: number;
	results: CaseResult[];
};

export type TestCase = {
	id: string;
	desc: string;
	ctx: Record<string, unknown>;
	intent?: string | null;
	expect: Record<string, unknown>;
};

export type TestsetResponse = {
	has_key: boolean;
	cases: TestCase[];
};

/** 节点输入字段规格:声明面(name/desc/shape/source/guard/status)+ 真实样例(prompt_samples 观测)。 */
export type PromptVar = {
	name: string;
	desc: string;
	shape?: string;
	source?: string;
	guard?: string;
	status?: "live" | "reserved";
	sample?: string;
	sample_empty?: boolean;
	sample_from?: string;
};
export type PromptPart = {
	value: string;
	default: string;
	overridden: boolean;
	variables: PromptVar[]; // 仅 user 段有(写 {{name}} 注入运行值)
};
/** 每节点拆 system(固定指令)/ user(变量+当前句)两段 */
export type PromptNode = { system: PromptPart; user: PromptPart };
export type PromptNodeKey = "n1" | "n2" | "n3";
export type PromptsResponse = Record<PromptNodeKey, PromptNode>;

export type TurnView = {
	msg: string;
	resume: string;
	intent: string | null;
	calls: string[];
	terminal: string | null;
	stop: string;
	clarify: string | null;
	committed: boolean;
	reply: string;
	trace: TraceStep[];
	node_trace?: NodeEvent[];
	timing?: Timing;
	blocks?: OutputBlock[];
};
export type SessionPending = { kind: string; slot: string | null } | null;
export type SessionFocus = { focus_order: string | null; focus_product: string | null };
export type SessionTurnResponse = {
	state: Record<string, unknown>;
	turn: TurnView;
	pending: SessionPending;
	focus: SessionFocus;
};

export function useSessionTurn() {
	return useMutation({
		mutationFn: async (body: {
			state: Record<string, unknown> | null;
			message: string;
			customer_id?: string;
		}) => {
			const { data } = await apiClient.post<SessionTurnResponse>(
				"/admin/v1/loop-lab/session/turn",
				body,
				{ timeout: 120_000 },
			);
			return data;
		},
	});
}

export type MtCaseResult = {
	id: string;
	desc: string;
	pass: boolean;
	fails: string[];
	turns?: TurnView[];
};
export type MtReport = { total: number; passed: number; results: MtCaseResult[] };

export function useRunMtOffline() {
	return useMutation({
		mutationFn: async () => {
			const { data } = await apiClient.post<MtReport>("/admin/v1/loop-lab/run-mt-offline");
			return data;
		},
	});
}

export function useRunMt() {
	return useMutation({
		mutationFn: async () => {
			const { data } = await apiClient.post<MtReport>("/admin/v1/loop-lab/run-mt", undefined, {
				timeout: 600_000,
			});
			return data;
		},
	});
}

// ---- 统一场景库(E3)----
export type ScenarioTurnSpec = {
	msg: string;
	resume: string;
	expect: Record<string, unknown>;
};
export type Scenario = {
	id: string;
	desc: string;
	kind: "single" | "mt_offline" | "mt_llm";
	type: string; // 售前/售中/售后/其他
	intent: string | null;
	customer_id: string;
	multi: boolean;
	modes: ("offline" | "llm")[];
	golden_skills: string[];
	turns: ScenarioTurnSpec[];
};
export type ScenarioCounts = {
	total: number;
	offline: number;
	llm: number;
	single: number;
	multi: number;
	by_type: Record<string, number>;
	by_intent: Record<string, number>;
};
export type ScenarioSelfcheck = {
	intents: number;
	skills: number;
	customers: number;
	orders: number;
	products: number;
	scenarios_total: number;
	scenarios_offline: number;
	scenarios_llm: number;
};
export type ScenariosResponse = {
	scenarios: Scenario[];
	counts: ScenarioCounts;
	selfcheck: ScenarioSelfcheck;
};
export type ScenarioRunResult = {
	id: string;
	desc: string;
	pass: boolean;
	fails: string[];
	multi: boolean;
	turns: TurnView[];
	type: string;
	intent: string | null;
	customer_id: string;
	golden_skills: string[];
};
export type ScenariosRunReport = {
	total: number;
	passed: number;
	results: ScenarioRunResult[];
	skipped: string[];
};

export function useScenarios() {
	return useQuery<ScenariosResponse>({
		queryKey: ["loop-lab", "scenarios"],
		queryFn: async () => {
			const { data } = await apiClient.get<ScenariosResponse>("/admin/v1/loop-lab/scenarios");
			return data;
		},
	});
}

export function useRunScenarios() {
	return useMutation({
		mutationFn: async (body: {
			ids: string[];
			mode: "offline" | "llm";
			with_n3?: boolean;
		}) => {
			const { data } = await apiClient.post<ScenariosRunReport>(
				"/admin/v1/loop-lab/scenarios/run",
				body,
				{ timeout: LONG },
			);
			return data;
		},
	});
}

export type IntentRow = {
	intent: string;
	skill: string | null;
	desc: string;
	boundary?: string;
	stage?: string; // 售前/售中/售后/通用(P2:与指南 scope.stage 同源 derive_intent_stage)
};
export type SkillInputField = { name: string; required: boolean; note: string };
export type SkillRow = {
	name: string;
	// desc 现在直接是注册表的 n2_desc(人机同一句)——此前另存一份 io_desc,9/14 已漂
	desc: string;
	// READ / WRITE / GENERIC:WRITE 蕴含 G5 确认门。此前后端一直在传、前端从不展示,
	// 「写操作·确认门」这个信号只活在 io_desc 的散文里 —— 删双源就得把它接出来
	tool_type: string;
	logic: string;
	intents: string[];
	required: string[];
	input: SkillInputField[];
	sample_input: Record<string, unknown>;
	sample_output: Record<string, unknown>;
};
export type ParamSource = { code: string; name: string; note: string };
export type SeedCustomer = {
	customer_id: string;
	name: string;
	email: string;
	bg: string;
	order_count: number;
};
export type SeedOrder = {
	orderId: string;
	customer_id: string | null;
	status: string | null;
	fulfillment_status: string | null;
	created_at: string | null;
	items: string;
};
export type SeedVariant = {
	variantId: string;
	options: Record<string, unknown>;
	price: number;
	inventory: number;
};
export type SeedProduct = {
	productId: string;
	title: string;
	discontinued: boolean;
	variants: SeedVariant[];
};
export type SeedView = {
	customers: SeedCustomer[];
	orders: SeedOrder[];
	products: SeedProduct[];
};
export type CatalogResponse = {
	intents: IntentRow[];
	skills: SkillRow[];
	seed: SeedView;
	return_reasons: string[];
	param_sources: ParamSource[];
};

export type HistoryRecord = {
	id: string;
	ts: string;
	mode: string;
	source: string;
	multi?: boolean; // 来自多轮场景/会话(应答效果统计「轮次」维度)
	message: string;
	ctx: Record<string, unknown>;
	intent: string | null;
	calls: string[];
	stop: string | null;
	clarify: string | null;
	committed: boolean;
	reply: string;
	trace: TraceStep[];
	node_trace?: NodeEvent[];
	timing?: Timing;
	blocks?: OutputBlock[];
	passed: boolean | null;
	fails: string[];
	contract: Record<string, unknown> | null;
};

export function useHistory(limit = 200) {
	return useQuery<{ records: HistoryRecord[] }>({
		queryKey: ["loop-lab", "history", limit],
		queryFn: async () => {
			const { data } = await apiClient.get<{ records: HistoryRecord[] }>(
				"/admin/v1/loop-lab/history",
				{ params: { limit } },
			);
			return data;
		},
	});
}

export function useClearHistory() {
	return useMutation({
		mutationFn: async () => {
			const { data } = await apiClient.delete<{ cleared: boolean }>("/admin/v1/loop-lab/history");
			return data;
		},
	});
}

export function useCatalog() {
	return useQuery<CatalogResponse>({
		queryKey: ["loop-lab", "catalog"],
		queryFn: async () => {
			const { data } = await apiClient.get<CatalogResponse>("/admin/v1/loop-lab/catalog");
			return data;
		},
	});
}

export function useTestset() {
	return useQuery<TestsetResponse>({
		queryKey: ["loop-lab", "testset"],
		queryFn: async () => {
			const { data } = await apiClient.get<TestsetResponse>("/admin/v1/loop-lab/testset");
			return data;
		},
	});
}

export function usePrompts() {
	return useQuery<PromptsResponse>({
		queryKey: ["loop-lab", "prompts"],
		queryFn: async () => {
			const { data } = await apiClient.get<PromptsResponse>("/admin/v1/loop-lab/prompts");
			return data;
		},
	});
}

export function useSavePrompts() {
	return useMutation({
		// 键 '{node}.{part}'(如 'n1.system');空串=重置回默认
		mutationFn: async (prompts: Record<string, string>) => {
			const { data } = await apiClient.put<{ overridden: string[] }>("/admin/v1/loop-lab/prompts", {
				prompts,
			});
			return data;
		},
	});
}

// ── 评测侧提示词(机器顾客 + 两判官)—— 独立 /eval-prompts,与被测 /prompts 互不串 ──
/** 每个评测提示词:label/purpose + 各段(user_sim 仅 system;判官有 system+user)。段形状同 PromptPart */
export type EvalPromptEntry = {
	label: string;
	purpose: string;
	parts: Partial<Record<"system" | "user", PromptPart>>;
};
/** keys: user_sim / nl_judge / attr */
export type EvalPromptsResponse = Record<string, EvalPromptEntry>;

export function useEvalPrompts() {
	return useQuery<EvalPromptsResponse>({
		queryKey: ["loop-lab", "eval-prompts"],
		queryFn: async () => {
			const { data } = await apiClient.get<EvalPromptsResponse>("/admin/v1/loop-lab/eval-prompts");
			return data;
		},
	});
}

export function useSaveEvalPrompts() {
	return useMutation({
		// 键 '{key}.{part}'(如 'user_sim.system' / 'nl_judge.user');空串=重置回默认
		mutationFn: async (prompts: Record<string, string>) => {
			const { data } = await apiClient.put<{ overridden: string[] }>(
				"/admin/v1/loop-lab/eval-prompts",
				{ prompts },
			);
			return data;
		},
	});
}

// ── 评测规则(确定性判分逻辑只读展示)—— 取自后端真实常量/函数 ──
export type EvalRuleDim = {
	key: string;
	label: string;
	kind: "rule" | "llm";
	enforced_by: string;
	prompt_ref?: string;
	fields: string[];
	how: string;
};
export type EvalRules = {
	dims: EvalRuleDim[];
	all_dims: string[];
	reward: { formula: string; none_note: string };
	reward_basis: { rule: string };
	attribution: { purpose: string; prompt_ref: string };
};

export function useEvalRules() {
	return useQuery<EvalRules>({
		queryKey: ["loop-lab", "eval-rules"],
		queryFn: async () => {
			const { data } = await apiClient.get<EvalRules>("/admin/v1/loop-lab/eval-rules");
			return data;
		},
	});
}

/** 应答配置·规则(C23 档1):六域规则注册表 + 活配置快照,只读忠实(源自 rules.RULE_REGISTRY)。 */
export type AnswerRule = {
	id: string;
	name: string;
	domain: string;
	where: string;
	mechanism: string;
	serves: string;
	generic: "ok" | "limit" | "custom";
	// C23 档3 触发承接矩阵:哪个环节 / 什么条件触发 / 输出去哪·承接哪步(注册表必填,单测锁)
	stage: string;
	when: string;
	output_to: string;
	note?: string;
	trace?: string | null;
	refs?: string;
	// 业务层(规则页第一层,注册表同源;完整性有后端单测锁)
	biz_role: string;
	biz_example: string;
	// 字段级 IO:从真卡观测(rule_io),不手写 —— output_to 是散文,答不出"吃什么吐什么"
	// io_channel:这条规则**有没有观测通道**(自有 trace / B2.5 resolve / 无观测通道)。
	// 与"有通道但没触发"必须分开显示,否则现状展示是假的。
	io_channel: string;
	io_observed: boolean;
	io_hits: number;
	io_in_keys: string[];
	io_out_keys: string[];
	io_sample: { input: string; output: string; from: string } | null;
};
export type AnswerRules = {
	domains: string[];
	domain_meta: Record<string, { biz: string; desc: string }>;
	registry: AnswerRule[];
	config: {
		required: Record<string, string[]>;
		intent_skill: Record<string, string | null>;
		skill_type: Record<string, string>;
		chain_prefetch: Record<string, string>;
		u_slot: Record<string, string>;
		exclude_rules: Record<string, { when_constraints: string[]; swap_to: string; why: string }>;
		budget: number;
		n2_base_skills: string[];
		scalar_keys: string[];
		digest_max: number;
	};
};

/** C26 链路总览:图结构+节点说明(后端单源聚合,规则节点直引注册表) */
export type LoopMapNode = {
	id: string;
	name: string;
	kind: "llm" | "rule" | "tool" | "flow";
	definition: string;
	actual: string;
	where: string;
	refs?: string;
	stage?: string | null;
	when?: string | null;
	output_to?: string | null;
	trace?: string | null;
};
export type LoopMapResponse = {
	graph: {
		session_lane: string[];
		main_lane: string[];
		input_lane: string[];
		loop: {
			budget: string;
			steps: string[];
			n2_guards: string[];
			b25_resolve: string[];
			skill_gates: string[];
			stop_outs: string[];
		};
		n1_fallback: string[];
		n1_outputs: string[];
		n3_inputs: string[];
		config: string[];
	};
	nodes: Record<string, LoopMapNode>;
};

export function useLoopMap() {
	return useQuery<LoopMapResponse>({
		queryKey: ["loop-lab", "loop-map"],
		queryFn: async () => {
			const { data } = await apiClient.get<LoopMapResponse>("/admin/v1/loop-lab/loop-map");
			return data;
		},
	});
}

export function useAnswerRules() {
	return useQuery<AnswerRules>({
		queryKey: ["loop-lab", "answer-rules"],
		queryFn: async () => {
			const { data } = await apiClient.get<AnswerRules>("/admin/v1/loop-lab/answer-rules");
			return data;
		},
	});
}

export function useRunTestset() {
	return useMutation({
		mutationFn: async (body: {
			mode: "offline" | "llm";
			with_n3: boolean;
			ids?: string[];
		}) => {
			const { data } = await apiClient.post<RunReport>("/admin/v1/loop-lab/run", body, {
				timeout: LONG,
			});
			return data;
		},
	});
}

export function useRunOne() {
	return useMutation({
		mutationFn: async (body: {
			desc: string;
			ctx?: Record<string, unknown>;
			with_n3: boolean;
		}) => {
			const { data } = await apiClient.post<CaseResult & { contract?: unknown }>(
				"/admin/v1/loop-lab/run-one",
				body,
				{ timeout: LONG },
			);
			return data;
		},
	});
}

// ---- C5 评测(机器顾客 × 多维判分 × pass^k)----
export type SimPersona = { name: string; desc: string };
export type SimScenarioEval = {
	expect_skills?: string[];
	forbid_skills?: string[];
	communicate_info?: string[];
	nl_assertions?: string[];
	reward_basis?: string[];
	// 指南契约(刀D M4:场景库覆盖率统计用)
	guidance_assertions?: Array<{ guideline_id: string; assertion: string; polarity: string }>;
	guidance_recall_expected?: string[];
};
/** 场景 live 可测性(V2-P1.6):真店环境能不能跑 + 罐头→真店订单号映射 */
export type SimScenarioLive = {
	ready: boolean;
	mode: "as-is" | "mapped" | "blocked";
	reason: string;
	order_map?: Record<string, string>;
};
export type SimScenario = {
	id: string;
	live?: SimScenarioLive;
	desc: string;
	stage: string; // 售前/售中/售后/其他
	intent: string;
	difficulty: string; // 缺信息/多候选/已发货不可/越权诱导/红线/顺畅
	category?: string | null; // 品类(tau2 搬入题:数码3C/家居/运动/多品类…;鞋服题为空)
	known_issue?: { cat: string; note: string; ref: string } | null; // 已诊断根因标记(C11 台账)
	persona: string | null;
	customer_id: string;
	data_refs: { orders: string[]; products: string[] };
	reason_for_call: string;
	known_info: string;
	unknown_info: string;
	task_instructions: string;
	eval: SimScenarioEval;
};
export type SimScenariosResponse = {
	has_key: boolean;
	personas: SimPersona[];
	scenarios: SimScenario[];
};

export type NlResult = { assertion: string; met: boolean; reason: string };
export type AttrError = { turn: number | null; severity: string; source: string; desc: string };
export type SimAttribution = {
	errors: AttrError[];
	first_critical_source: string;
	has_critical: boolean;
};
/** 判官过程(C7-T3):一次判官 LLM 调用的真实 I/O —— 送入的 system/user prompt + 裁决 JSON。 */
export type JudgeTraceEntry = {
	kind: string; // nl / attr
	label: string;
	model: string;
	system: string;
	user: string;
	result: Record<string, unknown>;
};
export type SimCard = {
	scenario_id: string;
	persona: string | null;
	termination_reason: string;
	committed?: boolean;
	reward: number;
	reward_basis: string[];
	failed_dims: string[];
	dims: { action: boolean | null; communicate: boolean | null; nl: boolean | null };
	action: {
		met: boolean;
		missing: string[];
		forbidden_called: string[];
		expected: string[];
		actual: string[];
	};
	communicate: { met: boolean; items: { value: string; said: boolean }[] };
	nl: { met: boolean; results: NlResult[] };
	num_turns?: number;
	turns?: TurnView[]; // 复用三栏钻取
	transcript?: { role: string; text: string }[];
	attribution?: SimAttribution;
	judge_trace?: JudgeTraceEntry[]; // C7-T3:判官真实 LLM I/O
	// C22-P0 机器顾客脚本核验(确定性):演员没说出 task_instructions 引号内关键句 → 失职标记
	// C22-P1 E:soft=数字全命中(疑似改写非漏说);violation 仅失败卡置位
	actor_check?: { scripted_quotes: { scripted: string; appeared: boolean; soft?: boolean }[] };
	actor_violation?: boolean;
	// 刀A/D:指南评测结果与分组标签
	guidance?: { hits: string[]; applied: string[] };
	guidance_verdicts?: Array<{
		guideline_id: string;
		polarity: string;
		met: boolean;
		reason: string;
		hit: boolean;
		applied: boolean;
		source?: string;
	}>;
	guidance_recall?: { expected: string[]; checked: string[]; missing: string[]; ok: boolean };
	stage?: string;
	difficulty?: string;
};
export type SimScenarioDetail = {
	scenario_id: string;
	passes: boolean[];
	pass_hat: Record<string, number>; // {"1":..,"2":..}
	cards: SimCard[];
};
export type SimAggregate = {
	n: number;
	avg_reward: number;
	dim_pass: { action: number | null; communicate: number | null; nl: number | null };
	termination: Record<string, number>;
	committed_rate: number;
};
export type SimRunReport = {
	trials: number;
	detail: SimScenarioDetail[];
	pass_k_curve: Record<string, number>;
	aggregate: SimAggregate;
	run_id?: string; // 落库后回填(C6-C)
	ts?: string;
};

// 评测历史批次(C6-C:评测落自己的库,与单轮「记录」分离)
export type GuidanceRunSummary = {
	verdicts_met: number;
	verdicts_total: number;
	recall_misses: Array<{ scenario_id: string; missing: string[] }>;
	stats: Record<string, { hit: number; applied: number; followed: number; violated: number }>;
} | null;

export type SimRunSummary = {
	id: string;
	ts: string;
	scenario_ids: string[];
	n_scenarios: number;
	trials: number;
	persona: string | null;
	aggregate: SimAggregate;
	pass_k_curve: Record<string, number>;
	// 刀A/刀D:指南结果摘要 / 分组矩阵 / 环境头 / 对照批标记 / 批次 CX(join)
	guidance_summary?: GuidanceRunSummary;
	by?: Record<string, Record<string, SimAggregate>>;
	env?: { actor_violations?: number; guidance_eval?: boolean } | null;
	baseline_of?: string | null;
	cx_positive?: number | null;
};

/** 指南效果页(M1/M7)数据:GET /guidance/stats?days */
export type GuidanceStats = {
	days: number;
	runs_used: number;
	kpi: { coverage: number | null; adoption: number | null; compliance: number | null };
	guidelines: Array<{
		id: string;
		title?: string;
		target_node?: string;
		status?: string;
		funnel: { hit: number; applied: number; followed: number; violated: number };
		quadrant: Record<string, number> | null;
	}>;
	recall_misses: Array<{ scenario_id: string; missing: string[]; run_id?: string }>;
};

export type TestSetInfo = {
	name: string;
	desc?: string;
	n: number;
	ids: string[];
	defaults: Record<string, boolean>;
};

export function useTestSets() {
	return useQuery<{ sets: TestSetInfo[] }>({
		queryKey: ["loop-lab", "test-sets"],
		queryFn: async () => {
			const { data } = await apiClient.get<{ sets: TestSetInfo[] }>(
				"/admin/v1/loop-lab/sim/test-sets",
			);
			return data;
		},
	});
}

export function useGuidanceStats(days = 7) {
	return useQuery<GuidanceStats>({
		queryKey: ["loop-lab", "guidance-stats", days],
		queryFn: async () => {
			const { data } = await apiClient.get<GuidanceStats>("/admin/v1/loop-lab/guidance/stats", {
				params: { days },
			});
			return data;
		},
	});
}

export function useSimRuns() {
	return useQuery<{ runs: SimRunSummary[] }>({
		queryKey: ["loop-lab", "sim-runs"],
		queryFn: async () => {
			const { data } = await apiClient.get<{ runs: SimRunSummary[] }>(
				"/admin/v1/loop-lab/sim/runs",
			);
			return data;
		},
	});
}

// ---- C7-S2 统一记录(三模式归一 · 摘要列表 + 详情按需) ----
export type RecordSummary = {
	rid: string;
	ts: string;
	test_mode: "manual" | "testset" | "sim";
	intent: string | null;
	customer: string;
	skill: string | null;
	calls: string[];
	stop: string | null;
	committed: boolean;
	result: string;
	msg: string;
	multi: boolean;
	// 人工多轮会话(后端已塌缩:一行=一次对话);turns=轮数,group=会话键;单发/旧记录为空
	group?: string | null;
	turn_no?: number | null;
	turns?: number;
	// 应答指南(P2):本条命中/采纳条数;无指南数据的旧记录缺省
	g_hits?: number;
	g_applied?: number;
};
export type RecordsResponse = { total: number; records: RecordSummary[] };
/** 详情:history 完整记录(单轮,带 node_trace)或 sim 批次内一条 trial(card+轨迹) */
export type RecordDetail =
	| (HistoryRecord & { test_mode?: undefined })
	| { test_mode: "sim"; run_id: string; ts: string; card: SimCard }
	// 人工多轮会话:整段各轮全量(轮次升序),抽屉一次看整个对话
	| { test_mode: "manual_session"; group: string; records: HistoryRecord[] };

export function useRecords(limit = 500) {
	return useQuery<RecordsResponse>({
		queryKey: ["loop-lab", "records", limit],
		queryFn: async () => {
			const { data } = await apiClient.get<RecordsResponse>("/admin/v1/loop-lab/records", {
				params: { limit },
			});
			return data;
		},
	});
}

export function useRecordDetail(rid: string | null) {
	return useQuery<RecordDetail>({
		queryKey: ["loop-lab", "record-detail", rid],
		enabled: !!rid,
		queryFn: async () => {
			const { data } = await apiClient.get<RecordDetail>("/admin/v1/loop-lab/record-detail", {
				params: { rid },
			});
			return data;
		},
	});
}

export function useSimRun(runId: string | null) {
	return useQuery<SimRunReport>({
		queryKey: ["loop-lab", "sim-run", runId],
		enabled: !!runId,
		queryFn: async () => {
			// run_id 含 : + # 等 URL 特殊字符(如 2026-07-02T02:21:45+00:00#25),必须整体转义,
			// 否则 # 被当成 URL 锚点截断 → 请求成 /sim/runs/...T02:21:45+00:00 → 404
			const { data } = await apiClient.get<SimRunReport>(
				`/admin/v1/loop-lab/sim/runs/${encodeURIComponent(runId ?? "")}`,
			);
			return data;
		},
	});
}

export function useSimScenarios() {
	return useQuery<SimScenariosResponse>({
		queryKey: ["loop-lab", "sim-scenarios"],
		queryFn: async () => {
			const { data } = await apiClient.get<SimScenariosResponse>(
				"/admin/v1/loop-lab/sim/scenarios",
			);
			return data;
		},
	});
}

export function useRunSim() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async (body: {
			scenario_ids: string[];
			trials: number;
			persona?: string | null;
			attribute?: boolean;
			allow_non_fixture?: boolean; // 环境防呆:非全罐头须显式放行
			with_cx?: boolean; // 刀A:CX 批量接入
			auto_check?: boolean; // 刀B:must 指南兜底核验(无显式断言场景)
			only_set?: string; // 刀E:命名测试集(展开 ids,集合 defaults 生效)
			compare?: Record<string, string>; // 刀A:对照维度字典(首维 guidance=off)
		}) => {
			const { data } = await apiClient.post<SimRunReport>("/admin/v1/loop-lab/sim/run", body, {
				timeout: LONG,
			});
			return data;
		},
		onSuccess: () => qc.invalidateQueries({ queryKey: ["loop-lab", "sim-runs"] }),
	});
}

// ---- C20 根因分析(RCA)----
/** rca-v2:证据引文 = 字段路径 + 原文摘录 + 为什么支撑 */
export type RcaEvidenceQuote = { ref?: string; quote?: string; why?: string };
export type RcaGroupHypothesis = {
	cluster: string;
	verdict: string; // yes | no | maybe
	sub_label?: string;
	node_locus?: string;
	evidence_refs?: string[];
	evidence_quotes?: RcaEvidenceQuote[];
	reason?: string;
	confidence?: number;
};
export type RcaVerdict = {
	primary_label: string;
	primary_cause_statement?: string; // rca-v2:一句人话第一性主因
	root_cause_chain?: string[]; // rca-v2:现象→直接原因→根本原因
	cluster: string;
	node_locus?: string;
	secondary_labels?: string[];
	evidence_refs?: string[];
	evidence_quotes?: RcaEvidenceQuote[];
	excluded_reasoning?: string;
	fix_direction?: string;
	confidence?: number;
	needs_human_review?: boolean;
	// v2.2:确定性置信校准兜底(≥2 被测簇 yes → 强制需复核+置信封顶),记录原值保持透明
	calibration?: { rule: string; tested_yes?: string[]; original_confidence?: number };
};
export type RcaRecordSummary = {
	id: string;
	ts: string;
	run_id: string;
	scenario_id: string;
	trial_index: number;
	reward: number;
	primary_label?: string;
	primary_cause_statement?: string;
	cluster?: string;
	node_locus?: string;
	confidence?: number;
	needs_human_review?: boolean;
};
export type RcaRecord = RcaRecordSummary & {
	failed_dims: string[];
	evidence_digest: Record<string, unknown>;
	group_hypotheses: RcaGroupHypothesis[];
	verdict: RcaVerdict;
	model?: string;
	prompt_version?: string;
	human_review?: unknown;
};
export type RcaStandardLabel = { key: string; four: string; judge: string; boundary: string };
export type RcaStandardCluster = { cluster: string; note: string; labels: RcaStandardLabel[] };
export type RcaEvidenceField = { path: string; desc: string };
export type RcaModelOption = { key: string; label: string };
export type RcaStandardResponse = {
	standard: RcaStandardCluster[];
	order: string;
	prompt_version: string;
	evidence_fields?: RcaEvidenceField[];
	model?: string;
	model_options?: RcaModelOption[];
};
export type RcaLintFinding = {
	scenario_id: string;
	category?: string;
	known_issue: boolean;
	missing_communicate_values: string[];
	foreign_data_refs: string[];
	hint: string;
};
export type RcaLintResponse = { checked: number; findings: RcaLintFinding[] };

export function useRcaRecords() {
	return useQuery<{ records: RcaRecordSummary[] }>({
		queryKey: ["loop-lab", "rca-records"],
		queryFn: async () => {
			const { data } = await apiClient.get<{ records: RcaRecordSummary[] }>(
				"/admin/v1/loop-lab/rca/records",
			);
			return data;
		},
	});
}

export function useRcaRecord(recId: string | null) {
	return useQuery<RcaRecord>({
		queryKey: ["loop-lab", "rca-record", recId],
		enabled: !!recId,
		queryFn: async () => {
			const { data } = await apiClient.get<RcaRecord>(
				`/admin/v1/loop-lab/rca/records/${encodeURIComponent(recId ?? "")}`,
			);
			return data;
		},
	});
}

export function useRcaStandard() {
	return useQuery<RcaStandardResponse>({
		queryKey: ["loop-lab", "rca-standard"],
		queryFn: async () => {
			const { data } = await apiClient.get<RcaStandardResponse>("/admin/v1/loop-lab/rca/standard");
			return data;
		},
	});
}

export function useSetRcaModel() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async (model: string) => {
			const { data } = await apiClient.put<{ model: string; model_options: RcaModelOption[] }>(
				"/admin/v1/loop-lab/rca/model",
				{ model },
			);
			return data;
		},
		onSuccess: () => qc.invalidateQueries({ queryKey: ["loop-lab", "rca-standard"] }),
	});
}

/** 统一按节点模型切换 · 所有大模型节点(N1/N2/N3/演员/判官/归因)共用候选表,运行时生效 */
export type NodeModelOption = { key: string; label: string };
export type NodeModelInfo = {
	key: string;
	label: string;
	model: string;
	env_default: string;
	overridden: boolean;
};
export type NodeModels = { nodes: NodeModelInfo[]; options: NodeModelOption[] };

export function useNodeModels() {
	return useQuery<NodeModels>({
		queryKey: ["loop-lab", "node-models"],
		queryFn: async () => {
			const { data } = await apiClient.get<NodeModels>("/admin/v1/loop-lab/node-models");
			return data;
		},
	});
}

export function useSetNodeModel() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async (vars: { node: string; model: string }) => {
			const { data } = await apiClient.put<{ node: string; model: string; nodes: NodeModelInfo[] }>(
				"/admin/v1/loop-lab/node-model",
				vars,
			);
			return data;
		},
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: ["loop-lab", "node-models"] });
			qc.invalidateQueries({ queryKey: ["loop-lab", "rca-standard"] }); // rca 也读统一模型
		},
	});
}

// ---- SkillPort:技能执行 backend(V2-P1) ----
export type SkillPortSkill = {
	name: string;
	type: string;
	backend: string;
	live_domain: string;
	recorded_count: number;
	latest_recording: {
		params: Record<string, unknown>;
		res: Record<string, unknown>;
		ts?: string;
	} | null;
};
export type SkillPortProbe = {
	ts: string | null;
	ok: boolean;
	by_domain: Record<string, { ok: boolean; error_code: string }>;
};
export type SkillPortConfig = {
	config: { default: string; skills: Record<string, string>; replay_miss: string };
	backends: string[];
	miss_policies: string[];
	skills: SkillPortSkill[];
	recordings: { total: number; by_skill: Record<string, number>; cap: number };
	probe: SkillPortProbe | null;
	merchant: ConnectMerchant;
	last_change: { ts: string; before: unknown; after: unknown } | null;
	snapshot: { default: string; skills: Record<string, string>; non_fixture: string[] };
};

export function useSkillPort() {
	return useQuery<SkillPortConfig>({
		queryKey: ["loop-lab", "skillport"],
		queryFn: async () => {
			const { data } = await apiClient.get<SkillPortConfig>("/admin/v1/loop-lab/skillport");
			return data;
		},
	});
}

export function useSetSkillPort() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async (vars: {
			default?: string;
			skills?: Record<string, string>;
			replay_miss?: string;
		}) => {
			const { data } = await apiClient.put<SkillPortConfig>("/admin/v1/loop-lab/skillport", vars);
			return data;
		},
		onSuccess: () => qc.invalidateQueries({ queryKey: ["loop-lab", "skillport"] }),
	});
}

export function useClearRecordings() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async () => {
			const { data } = await apiClient.delete<{ ok: boolean }>(
				"/admin/v1/loop-lab/skillport/recordings",
			);
			return data;
		},
		onSuccess: () => qc.invalidateQueries({ queryKey: ["loop-lab", "skillport"] }),
	});
}

// ---- connect:Shopify 连接层(V2-P1 PR-3) ----
export type ConnectMerchant = {
	label: string;
	shop_domain: string;
	api_version: string;
	auth_mode: string;
	enabled: boolean;
	token_configured: boolean;
};
export type ConnectInfo = {
	merchant: ConnectMerchant;
	auth_modes: string[];
	key_fingerprint: string;
	probe_history: Array<{ ts: string; ok: boolean; items: ConnectProbe["items"] }>;
};
export type ConnectProbe = {
	merchant: ConnectMerchant;
	items: Array<{ name: string; ok: boolean; error_code: string; detail: string }>;
	ok: boolean;
};

export function useConnect() {
	return useQuery<ConnectInfo>({
		queryKey: ["loop-lab", "connect"],
		queryFn: async () => {
			const { data } = await apiClient.get<ConnectInfo>("/admin/v1/loop-lab/connect");
			return data;
		},
	});
}

export function useSetConnect() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async (vars: Partial<Omit<ConnectMerchant, "token_configured">>) => {
			const { data } = await apiClient.put<ConnectInfo>("/admin/v1/loop-lab/connect", vars);
			return data;
		},
		onSuccess: () => qc.invalidateQueries({ queryKey: ["loop-lab", "connect"] }),
	});
}

export function useConnectProbe() {
	return useMutation({
		mutationFn: async () => {
			const { data } = await apiClient.post<ConnectProbe>("/admin/v1/loop-lab/connect/probe");
			return data;
		},
	});
}

export function useRcaContractLint(enabled: boolean) {
	return useQuery<RcaLintResponse>({
		queryKey: ["loop-lab", "rca-contract-lint"],
		enabled,
		queryFn: async () => {
			const { data } = await apiClient.get<RcaLintResponse>("/admin/v1/loop-lab/rca/contract-lint");
			return data;
		},
	});
}

export function useRunRca() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async (body: { run_id: string; scenario_id: string; trial_index?: number }) => {
			const { data } = await apiClient.post<RcaRecord>("/admin/v1/loop-lab/rca/analyze", body, {
				timeout: LONG,
			});
			return data;
		},
		onSuccess: () => qc.invalidateQueries({ queryKey: ["loop-lab", "rca-records"] }),
	});
}

// ---- CX 会话级评分(E_CX_Score_Loop会话级评测) ----
/** 7 reasons 取值(方向语义由后端 /cx-score/standard 注册表下发,前端不硬编码) */
export type CxReasonsMap = Record<string, string>;
export type CxRecord = {
	id: string;
	ts: string;
	rid: string;
	rid_raw?: string | null;
	test_mode: string;
	run_id?: string | null;
	scenario_id?: string | null;
	trial_index?: number | null;
	num_turns: number;
	first_msg: string;
	cx_score: number;
	reasons: CxReasonsMap;
	explanation: string;
	calibration?: { rule: string; original_score: number } | null;
	needs_review: boolean;
	review_rules: string[];
	meta_used?: Record<string, unknown>;
	model: string;
	prompt_version: string;
	prompt_override: boolean;
};
export type CxRecordsResponse = { records: CxRecord[]; latest: Record<string, CxRecord> };
export type CxSummary = {
	scored_sessions: number;
	overall_cx: number;
	score_dist: Record<string, number>;
	negative_reasons: Record<string, number>;
	needs_review: number;
};
export type CxMetaField = {
	key: string;
	label: string;
	history_source: string;
	sim_source: string;
	informs: string[];
	prompt_note: string;
};
export type CxReasonMeta = {
	key: string;
	label: string;
	values: string[];
	negative_when: string;
	positive_when: string;
	signal: string;
	rubric_block: string;
};
export type CxStandard = {
	meta_fields: CxMetaField[];
	reasons: CxReasonMeta[];
	signals: Record<string, string>;
	calibration_rules: { id: string; desc: string; action: string }[];
	eligibility: string;
	positive_rule: string;
	prompt_version: string;
	prompt_override: boolean;
	prompts: Record<string, string>;
	model: string;
};

export function useCxRecords() {
	return useQuery<CxRecordsResponse>({
		queryKey: ["loop-lab", "cx-records"],
		queryFn: async () => {
			const { data } = await apiClient.get<CxRecordsResponse>(
				"/admin/v1/loop-lab/cx-score/records",
			);
			return data;
		},
	});
}

export function useCxRecordDetail(recId: string | null) {
	return useQuery<CxRecord>({
		queryKey: ["loop-lab", "cx-record", recId],
		enabled: !!recId,
		queryFn: async () => {
			const { data } = await apiClient.get<CxRecord>(
				`/admin/v1/loop-lab/cx-score/records/${encodeURIComponent(recId ?? "")}`,
			);
			return data;
		},
	});
}

export function useCxSummary(days = 0) {
	return useQuery<CxSummary>({
		queryKey: ["loop-lab", "cx-summary", days],
		queryFn: async () => {
			const { data } = await apiClient.get<CxSummary>("/admin/v1/loop-lab/cx-score/summary", {
				params: { days },
			});
			return data;
		},
	});
}

export function useCxStandard() {
	return useQuery<CxStandard>({
		queryKey: ["loop-lab", "cx-standard"],
		queryFn: async () => {
			const { data } = await apiClient.get<CxStandard>("/admin/v1/loop-lab/cx-score/standard");
			return data;
		},
	});
}

export function useRunCxScore() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async (body: { rid: string }) => {
			const { data } = await apiClient.post<CxRecord>("/admin/v1/loop-lab/cx-score/analyze", body, {
				timeout: LONG,
			});
			return data;
		},
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: ["loop-lab", "cx-records"] });
			qc.invalidateQueries({ queryKey: ["loop-lab", "cx-summary"] });
		},
	});
}

// ---- C35 概览统计 ----
export type OverviewKpi = {
	/** 指标契约(刀A):率类一律 ratio(0-1),换算成百分数是前端的事。 */
	unit?: "ratio" | "count" | "token" | "ms";
	value: number | null;
	prev: number | null;
	spark: (number | null)[];
	/** 率的分母(已判定条数);total 是执行条数,两者不同源时脚注要写清 */
	n?: number;
	total?: number;
	calls?: number;
	needs_review_rate?: number | null;
	cost_usd_est?: number | null;
	/** 成本覆盖度:估算只覆盖单价表内的模型,漏算面必须显形 */
	priced_calls?: number;
	unpriced_models?: Record<string, number>;
	/** 判官标定专有:不同金标集不可比,必须写明这个数量在哪一集上量的 */
	gold_set?: string | null;
	ts?: string | null;
	false_alarm?: number | null;
	miss?: number | null;
};
export type OverviewByDate = {
	date: string;
	/** 刀B:率旁边带失败构成——卡在澄清还是转人工,分布里看得出 */
	answer: {
		total: number;
		n: number;
		rate: number | null;
		stop_dist: Record<string, number>;
		clarify_rate: number | null;
	};
	timing_p95: number | null;
	eval: { trials: number; pass_rate: number | null; failed_dims: Record<string, number> };
	cx: {
		scored: number;
		positive_rate: number | null;
		reason_dist: Record<string, Record<string, number>>;
	};
	rca: { count: number };
	tokens: { total: number };
	/** 刀C:评测装置健康度(不是客服表现) */
	actor: { n: number; rate: number | null };
	guidance: { n: number; rate: number | null };
	judge: { n: number; rate: number | null; gold_set: string | null };
};
export type StatsOverview = {
	days: number;
	kpis: {
		answer_rate: OverviewKpi;
		eval_pass: OverviewKpi;
		cx_positive: OverviewKpi;
		rca_count: OverviewKpi;
		tokens_total: OverviewKpi;
		timing_p95: OverviewKpi;
		judge_agreement: OverviewKpi;
		guidance_follow_rate: OverviewKpi;
		actor_violation_rate: OverviewKpi;
	};
	/** 刀C:被测侧(客服表现)/ 评测侧(尺子本身)分组——混着看会把判官退化读成客服变差 */
	groups: { subject: string[]; harness: string[] };
	by_date: OverviewByDate[];
};

// ── 判官标定(读 calibration_store)──────────────────────────────────────
export type CalibrationRow = {
	id: string;
	ts: string;
	gold_set: string;
	gold_set_label: string;
	gold_count: number;
	model: string;
	agreement: number | null;
	false_alarm: number;
	miss: number;
	hard_agreement: number | null;
	hard_n: number | null;
};

export type CalibrationVerdictRow = {
	id: string;
	guideline_id: string;
	tier: string;
	polarity: string;
	expected_met: boolean;
	judged_met: boolean | null;
	agree: boolean;
	judge_reason: string;
	reason_truncated?: boolean;
};

export type CalibrationDetail = {
	id: string;
	ts: string;
	gold_set_label: string;
	gold_count: number;
	model: string;
	summary_text: string;
	overall: Record<string, number | null>;
	by_tier: Record<string, Record<string, number | null>>;
	rows: CalibrationVerdictRow[];
};

export function useCalibrations(goldSet = "") {
	return useQuery<{ records: CalibrationRow[]; gold_sets: Record<string, string> }>({
		queryKey: ["loop-lab", "calibrations", goldSet],
		queryFn: async () => {
			const { data } = await apiClient.get("/admin/v1/loop-lab/guidance/calibrations", {
				params: { limit: 50, gold_set: goldSet },
			});
			return data;
		},
	});
}

export function useCalibrationDetail(id: string | null) {
	return useQuery<CalibrationDetail>({
		enabled: !!id,
		queryKey: ["loop-lab", "calibration-detail", id],
		queryFn: async () => {
			// 批次 id 含 `:` `#` `+`,必须编码后再进路径
			const { data } = await apiClient.get(
				`/admin/v1/loop-lab/guidance/calibrations/${encodeURIComponent(id ?? "")}`,
			);
			return data;
		},
	});
}

export function useStatsOverview(days: number) {
	return useQuery<StatsOverview>({
		queryKey: ["loop-lab", "stats-overview", days],
		refetchInterval: 30_000,
		queryFn: async () => {
			const { data } = await apiClient.get<StatsOverview>("/admin/v1/loop-lab/stats/overview", {
				params: { days },
			});
			return data;
		},
	});
}

// ── 应答指南(P2 PR-3)—— 详设 §8:有效集(seed∪overlay)+ CRUD,写入口硬校验 400 中文 ──
export type GuidanceEntry = {
	id: string;
	title?: string;
	layer: "platform" | "merchant" | "default";
	scope: { intents: string[]; stage?: string };
	condition: string;
	action: string;
	criticality?: "must" | "should" | "prefer";
	// 作用环节:talk=仅 N3 话术;decide=仅 N2 决策;both=两层(缺省 talk)
	target_node?: "talk" | "decide" | "both";
	// G19 前置反问门:must 指南可声明——写操作确认前保证问过一次该槽(如 returnReason)
	requires_slot?: string;
	rationale?: string;
	status?: "live" | "paused" | "draft";
	updated_at?: string;
	updated_by?: string;
};
export type GuidanceSnapshot = { count: number; live_ids: string[]; digest: string };
export type GuidanceResponse = {
	items: GuidanceEntry[];
	snapshot: GuidanceSnapshot;
	layers: string[];
	criticalities: string[];
	target_nodes: string[];
	stages: string[];
	intents: string[];
	limits: { max_action_len: number; max_live: number };
};

export function useGuidance() {
	return useQuery<GuidanceResponse>({
		queryKey: ["loop-lab", "guidance"],
		queryFn: async () => {
			const { data } = await apiClient.get<GuidanceResponse>("/admin/v1/loop-lab/guidance");
			return data;
		},
	});
}

export function useUpsertGuidance() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async (entry: GuidanceEntry) => {
			const { data } = await apiClient.put<{
				entry: GuidanceEntry;
				snapshot: GuidanceSnapshot;
				warnings?: string[];
			}>("/admin/v1/loop-lab/guidance", { entry });
			return data;
		},
		onSuccess: () => qc.invalidateQueries({ queryKey: ["loop-lab", "guidance"] }),
	});
}

export function useSetGuidanceStatus() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async (p: { id: string; status: "live" | "paused" | "draft" }) => {
			const { data } = await apiClient.put<{ entry: GuidanceEntry; snapshot: GuidanceSnapshot }>(
				`/admin/v1/loop-lab/guidance/${encodeURIComponent(p.id)}/status`,
				{ status: p.status },
			);
			return data;
		},
		onSuccess: () => qc.invalidateQueries({ queryKey: ["loop-lab", "guidance"] }),
	});
}

/* ---- 环节 IO 契约(P0):输入/输出字段规范 + 四面对齐审计,单源在代码,页面只渲染 ---- */
export type IoVar = {
	name: string;
	desc: string;
	shape: string;
	source: string;
	guard: string;
	status: "live" | "reserved";
};
export type IoOutput = { name: string; desc: string; to: string };
export type IoFinding = {
	kind: string;
	field: string;
	detail: string;
	level: string;
	stage?: string;
};
export type IoStage = {
	key: string;
	label: string;
	domain: string;
	site: string;
	note: string;
	faces: { declared: string[]; supplied: string[]; rendered: string[]; mentioned: string[] };
	inputs: IoVar[];
	outputs: IoOutput[];
	post_rules: string[];
	findings: IoFinding[];
	detail_hash: string;
	detail_label: string;
};
export type IoAssemblySurface = {
	id: string;
	name: string;
	where: string;
	produces: string;
	feeds: string;
	note: string;
};
export type IoDualSource = {
	id: string;
	what: string;
	source_a: string;
	source_b: string;
	status: string;
	why_open: string;
	id_mismatch: string[];
	drift_count: number;
	total: number;
	drift: { key: string; registry: string; prompt: string }[];
	detail_hash: string;
	detail_label: string;
};
export type IoRoadmapItem = {
	id: string;
	name: string;
	scope: string;
	status: string;
	ref: string;
};
export type IoOpportunity = {
	id: string;
	stage: string;
	field: string;
	kind: string;
	status: string;
	title: string;
	evidence: string;
	hypothesis: string;
	expected: string;
	risk: string;
	verify: string;
};
/** 六维:d1 静态四面 / d2 真卡核验 / d3 格式样例 / d4 观测通道 / d5 内容质量 / d6 单源与锁。
 *  旧模型只有 static/live 两列,表达不了「观测通道分类」「透出面单源化」这类实际做过的事,
 *  于是表永远显示"未做"、真实进度只活在 PR 里 —— 页面比代码更不能骗人。 */
export type IoDimension = { key: string; name: string; desc: string };
export type IoCoverageRow = {
	layer: string;
	target: string;
	d1: string;
	d2: string;
	d3: string;
	d4: string;
	d5: string;
	d6: string;
	note: string;
	detail_hash: string;
	detail_label: string;
};
export type IoBlocker = {
	id: string;
	title: string;
	blocks: string;
	evidence: string;
	fix: string;
};
export type IoDebt = { id: string; title: string; detail: string; fix: string };
export type IoContentTodo = { id: string; target: string; question: string; why: string };
export type IoContractResponse = {
	stages: IoStage[];
	roadmap: IoRoadmapItem[];
	coverage: IoCoverageRow[];
	dimensions: IoDimension[];
	blockers: IoBlocker[];
	process_debt: IoDebt[];
	content_audit_todo: IoContentTodo[];
	opportunities: IoOpportunity[];
	assembly_surfaces: IoAssemblySurface[];
	dual_sources: IoDualSource[];
	summary: {
		stage_count: number;
		input_field_count: number;
		output_field_count: number;
		assembly_surface_count: number;
		findings_by_kind: Record<string, number>;
		findings_total: number;
		opportunity_open: number;
		opportunity_pending_rca: number;
		defect_fixed: number;
		roadmap_done: number;
		roadmap_total: number;
		cells_done: number;
		cells_partial: number;
		cells_todo: number;
		cells_na: number;
		coverage_total: number;
		blockers: number;
		process_debt: number;
		content_audit_todo: number;
	};
	findings: IoFinding[];
};

export function useIoContract() {
	return useQuery<IoContractResponse>({
		queryKey: ["loop-lab", "io-contract"],
		queryFn: async () => {
			const { data } = await apiClient.get<IoContractResponse>("/admin/v1/loop-lab/io-contract");
			return data;
		},
	});
}

/* ---- 方法论 skill(.claude/skills 只读同源;版本历史=G1 更新日志表)---- */
export type MethodSkillUpdate = { date: string; change: string; source: string };
export type MethodSkill = {
	name: string;
	description: string;
	body: string;
	updates: MethodSkillUpdate[];
	latest_update: string | null;
	versions: number;
	path: string;
};

export function useMethodSkills() {
	return useQuery<{ skills: MethodSkill[] }>({
		queryKey: ["loop-lab", "method-skills"],
		queryFn: async () => {
			const { data } = await apiClient.get<{ skills: MethodSkill[] }>(
				"/admin/v1/loop-lab/method-skills",
			);
			return data;
		},
	});
}
